import React, { useState } from 'react';
import { View, Maximize2, X, Camera, Share2, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';

const VirtualTour = () => {
  const { t } = useLanguage();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // Professional 360° Virtual Tour from virtualno360.hr
  const virtualTourUrl = 'https://virtualtours.virtualno360.hr/F1vBALBgDW';

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Hotel Uma Premium - 360° Virtual Tour',
          text: 'Explore Hotel Uma Premium in Ulcinj, Montenegro with this interactive 360° virtual tour!',
          url: virtualTourUrl
        });
      } catch (err) {
        // User cancelled or error
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(virtualTourUrl);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <section id="virtual-tour" className="py-24 bg-gradient-to-b from-slate-900 to-slate-800 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-amber-400 font-medium tracking-widest text-sm uppercase">
            {t('virtualTour.subtitle') || 'Immersive Experience'}
          </span>
          <h2 className="text-4xl md:text-5xl font-serif text-white mt-3 mb-4">
            {t('virtualTour.title') || '360° Virtual Tour'}
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto text-lg">
            {t('virtualTour.description') || 'Explore every corner of Hotel Uma Premium from the comfort of your home. Navigate through our beautiful spaces with interactive 360° views.'}
          </p>
        </div>

        {/* Tour Viewer Container */}
        <div className="relative bg-slate-800 rounded-2xl overflow-hidden shadow-2xl">
          {/* Header Bar */}
          <div className="bg-gradient-to-r from-amber-600 to-amber-700 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <View className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Hotel Uma Premium Virtual Tour</h3>
                <p className="text-white/70 text-sm">{t('virtualTour.dragToExplore') || 'Click and drag to explore • Use scroll to zoom'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleShare}
                className="text-white hover:bg-white/20"
                data-testid="share-tour-btn"
                title="Share tour"
              >
                <Share2 className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsFullscreen(true)}
                className="text-white hover:bg-white/20"
                data-testid="fullscreen-btn"
                title="Fullscreen"
              >
                <Maximize2 className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* 360 Viewer */}
          <div className="relative h-[500px] md:h-[600px] bg-slate-900">
            {/* Loading State */}
            {!iframeLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-900 z-10">
                <div className="text-center">
                  <div className="w-16 h-16 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-slate-400">{t('virtualTour.loading') || 'Loading 360° virtual tour...'}</p>
                </div>
              </div>
            )}

            {/* Professional 360 Tour Embed */}
            <iframe
              src={virtualTourUrl}
              width="100%"
              height="100%"
              frameBorder="0"
              allowFullScreen
              title="Hotel Uma Premium 360° Virtual Tour"
              onLoad={() => setIframeLoaded(true)}
              className="absolute inset-0"
              data-testid="tour-iframe"
            />

            {/* Interaction Hint */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm flex items-center gap-2 z-20 pointer-events-none">
              <Camera className="w-4 h-4" />
              {t('virtualTour.dragToExplore') || 'Click and drag to explore • Scroll to zoom'}
            </div>
          </div>
        </div>

        {/* Features & CTA */}
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {/* Feature 1 */}
          <div className="bg-slate-800/50 rounded-xl p-6 text-center">
            <div className="w-14 h-14 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h4 className="text-white font-semibold mb-2">{t('virtualTour.pool') || 'Rooftop Pool'}</h4>
            <p className="text-slate-400 text-sm">{t('virtualTour.poolDesc') || 'Stunning rooftop pool with panoramic views'}</p>
          </div>

          {/* Feature 2 */}
          <div className="bg-slate-800/50 rounded-xl p-6 text-center">
            <div className="w-14 h-14 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h4 className="text-white font-semibold mb-2">{t('virtualTour.restaurant') || 'Restaurant'}</h4>
            <p className="text-slate-400 text-sm">{t('virtualTour.restaurantDesc') || 'Elegant dining with delicious cuisine'}</p>
          </div>

          {/* Feature 3 */}
          <div className="bg-slate-800/50 rounded-xl p-6 text-center">
            <div className="w-14 h-14 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <h4 className="text-white font-semibold mb-2">{t('virtualTour.rooms') || 'Guest Rooms'}</h4>
            <p className="text-slate-400 text-sm">{t('virtualTour.roomsDesc') || 'Comfortable and well-appointed rooms'}</p>
          </div>
        </div>

        {/* Open in New Tab Button */}
        <div className="text-center mt-8">
          <Button
            asChild
            className="bg-amber-700 hover:bg-amber-800 text-white px-8 py-6 rounded-xl text-lg shadow-lg hover:shadow-xl transition-all"
            data-testid="open-full-tour-btn"
          >
            <a href={virtualTourUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-5 h-5 mr-2" />
              {t('virtualTour.openFullTour') || 'Open Full 360° Tour in New Tab'}
            </a>
          </Button>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white z-10 transition-colors"
            data-testid="close-fullscreen-btn"
          >
            <X className="w-6 h-6" />
          </button>
          <iframe
            src={virtualTourUrl}
            width="100%"
            height="100%"
            frameBorder="0"
            allowFullScreen
            title="Hotel Uma Premium 360° Virtual Tour (Fullscreen)"
          />
        </div>
      )}
    </section>
  );
};

export default VirtualTour;
